import * as Sentry from "@sentry/react";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useGetCall } from "seed/api";
import { useDetail } from "seed/gql";
import { Loading } from "seed/helpers";

const AuthContext = createContext(undefined);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context)
    throw new Error("useAuth must be used within an AuthProvider");
  return context;
}

const AuthProvider = ({ children }) => {

  const [userId, setUserId] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [user, setUser] = useState(null);

  const reqUser = useDetail(`{
      user {
        id
        username
        firstName
        lastName
        email
        isActive
        phone
        isDeveloper
        isAnonymous
        tokenVerified
        accountSettings
        notificationSettings
        verifyToken
        leadToken
        verifyTokenExpiration
        wasOnboardingCompleted
        license {
          name
        }
      }
    }`, userId, {
    onCompleted: (data) => {
      const _user = Object.assign({}, data.user);
      sessionStorage.setItem("is_developer", _user.isDeveloper);
      setUser(_user);

      // Set Sentry user 
      Sentry.setUser(_user);
    },
    captureErrors: false
  });

  const [callAuth] = useGetCall("/auth/user", "", {
    onCompleted: (data) => {
      // Init execution session id with a new random value
      if (sessionStorage.getItem("execution_session_id") == null)
        sessionStorage.setItem("execution_session_id", Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15));

      // Set language preference
      if (data.account_settings?.language)
        localStorage.setItem("language", data.account_settings.language);

      setIsAuth(true);
      setUserId(data.id);
      reqUser.refetch();
    },
    onError: () => {
      // Which flow was open, kept across the login round trip; everything else is dropped.
      const flowToken = localStorage.getItem("flow_token");

      sessionStorage.clear();
      localStorage.clear();
      Sentry.setUser(null);

      if (flowToken) localStorage.setItem("flow_token", flowToken);
      window.location.replace(`/login?next=${encodeURIComponent(new URL(window.location.href).pathname)}`);
    },
    captureErrors: false
  });



  useEffect(() => {
    // Preload session data (URL / localStorage -> sessionStorage) before reading the id
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const paramUserId = urlParams.get("user_id");

    if (token) {
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("id", paramUserId);
      localStorage.setItem("token", token);
      localStorage.setItem("id", paramUserId);
    }

    if (localStorage.getItem("id") != null) {
      sessionStorage.setItem("token", localStorage.getItem("token"));
      sessionStorage.setItem("id", localStorage.getItem("id"));
    }

    callAuth({ user_id: sessionStorage.getItem("id") });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    sessionStorage.setItem("show_helps", user?.accountSettings?.showHelps ?? "true");
    sessionStorage.setItem("show_debug_locales", user?.accountSettings?.showDebugLocales ?? "true");
  }, [user]);

  useEffect(() => {
    reqUser.refetch();
  }, [userId]);

  if (!isAuth || !user)
    return <Loading />;

  return (
    <AuthContext.Provider value={user}>
      {children}
    </AuthContext.Provider>
  );

}

export { AuthProvider, useAuth };